import { format, getDay, isBefore, isWithinInterval as isWithinIntervalFns, nextDay, parse, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import sortBy from 'lodash/sortBy';

type DateArg = Date | number | string;
type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type HolidayHours = { closingTime: string; date: string; openingTime: string }[];
type Hours = { from: string; to: string; weekday?: Weekday };

const isStoreOpen = (hours: Hours, tenantTimeZone: string) => hours && isMenuWithinInterval(hours, tenantTimeZone);

const isMenuActive = (hours: Hours, strictHours: boolean, timeZone: string) => {
  if (strictHours) {
    return isMenuWithinInterval(hours, timeZone);
  }

  return isBefore(Date.now(), parse(get(hours, 'to'), 'HH:mm', new Date()));
};

const isMenuWithinInterval = (hours: Hours, timeZone: string) => {
  const currentTime = format(utcToZonedTime(new Date(), timeZone), 'HH:mm');

  return get(hours, 'from', '00:00') <= currentTime && currentTime <= get(hours, 'to', '00:00');
};

// eslint-disable-next-line default-param-last
const formatHoursInterval = (hours: Hours, template: string = 'HH:mm:ss', timeFormat: string) => {
  const from = format(parse(hours.from, template, new Date()), timeFormat);
  const to = format(parse(hours.to, template, new Date()), timeFormat);

  return `${from} - ${to}`;
};

const parseTimeToDate = (time: string) => {
  const date = parse(time, 'HH:mm', new Date());

  return date && !isNaN(date.getTime()) ? date : null;
};

const getCurrentWeekday = () => {
  const day = getDay(new Date());

  return day === 0 ? 7 : day;
};

const getNextWeekDay = (weekday: Weekday) => {
  const day = ((weekday || getCurrentWeekday()) + 1) as 2 | 3 | 4 | 5 | 6 | 7 | 8;

  return (day > 7 ? 1 : day) as Weekday;
};

const getWeekday = (date: DateArg) => {
  const day = getDay(new Date(date));

  return day === 0 ? 7 : day;
};

const isWithinInterval = (hours: Hours, time: string) =>
  get(hours, 'from', '00:00') <= time && time <= get(hours, 'to', '00:00');

const isWithinDates = (day: Date | number, dates?: { dateFrom: string, dateTo: string }) => {
  if (!dates || isEmpty(dates)) {
    return true;
  }

  const start = parseISO(dates.dateFrom);
  const end = parseISO(dates.dateTo);

  return isWithinIntervalFns(day, { start, end });
};

const findClosestFrom = (menuHours: Hours[], holidayHours: { openingTime: string }) => {
  const from = get(holidayHours, 'openingTime', '00:00');

  return menuHours.map(hours => {
    if (isWithinInterval(hours, from)) {
      return { ...hours, from };
    }

    if (hours.from >= from) {
      return hours;
    }

    return null;
  });
};

const findClosestTo = (menuHours: (Hours | null)[], holidayHours: { closingTime: string }) => {
  const to = get(holidayHours, 'closingTime', '00:00');

  return menuHours.map(hours => {
    if (!hours) {
      return null;
    }

    if (isWithinInterval(hours, to)) {
      return { ...hours, to };
    }

    if (hours.to <= to) {
      return hours;
    }

    return null;
  });
};

const calculateMenuOpeningHours = (
  date: Date | number,
  menuHours: Hours[],
  holidayDates: HolidayHours,
  hoursFormat = true
) => {
  if (isEmpty(menuHours)) {
    return null;
  }

  const holidayHours = (holidayDates || []).find(elm => elm.date === format(date, 'yyyy-MM-dd'));
  let openingHours = menuHours;

  if (holidayHours) {
    const closestFrom = findClosestFrom(menuHours, holidayHours);
    const closestTo = findClosestTo(closestFrom, holidayHours);

    openingHours = compact(closestTo);
  }

  if (hoursFormat) {
    return sortBy(
      openingHours.map(hours => ({ from: hours.from, to: hours.to })),
      ['from', 'to']
    );
  }

  return sortBy(openingHours, ['from', 'to']);
};

const getWeekdayWorkHours = (hours: Hours[], date?: DateArg) =>
  filter(hours, { weekday: getWeekday(date || new Date()) });

const getNextWeekdayWorkHours = (hours: Hours[], date: DateArg) =>
  filter(hours, { weekday: getNextWeekDay(getWeekday(date || new Date())) });

const getCurrentDayWorkHours = (hours: Hours[], timezone: string) => {
  const todayWorkHours = getWeekdayWorkHours(hours);

  if (isEmpty(todayWorkHours)) {
    return null;
  }

  const currentTime = format(utcToZonedTime(new Date(), timezone), 'HH:mm');

  return todayWorkHours.find(hours => isWithinInterval(hours, currentTime));
};

const getMenuHours = (
  menu: {
    availableHours: Hours[];
    futureDate: Date;
    futureDay: Date | null;
    menuOpeningHours?: { from: string; to: string }[];
  },
  holidayHours: HolidayHours,
  timezone: string,
  isFutureDay: boolean
) => {
  const { availableHours, futureDate, futureDay, menuOpeningHours } = menu;

  if (!menuOpeningHours) {
    return null;
  }

  const closestDate = isFutureDay ? futureDate || futureDay : new Date();
  const nextWeekDayDateFns = getNextWeekDay(getWeekday(closestDate));
  const nextWeekDay = nextWeekDayDateFns > 7 ? 1 : nextWeekDayDateFns;

  const availableHoursNextDay =
    closestDate &&
    nextWeekDay &&
    calculateMenuOpeningHours(
      // @ts-expect-error
      nextDay(closestDate, nextWeekDay),
      getNextWeekdayWorkHours(availableHours, closestDate),
      holidayHours
    );

  const startFromMidnightNextDay = availableHoursNextDay && availableHoursNextDay.find(hour => hour.from === '00:00');
  let openingHours = cloneDeep([...menuOpeningHours]);

  const isStartFromMidnight = isFutureDay
    // @ts-expect-error
    ? getWeekdayWorkHours(availableHours, closestDate)?.from === '00:00'
    : getCurrentDayWorkHours(availableHours, timezone)?.from === '00:00';

  const isEndToMidnight = openingHours.find(hour => hour.to === '23:59');

  if (
    !isFutureDay &&
    openingHours.length > 1 &&
    startFromMidnightNextDay &&
    !isStartFromMidnight &&
    !!isEndToMidnight
  ) {
    openingHours = openingHours.filter(hour => !(hour.from === '00:00'));
  }

  const joinedOpeningHours = openingHours.map(hour => {
    if (hour.to === '23:59' && startFromMidnightNextDay && startFromMidnightNextDay.to !== '00:00') {
      return { ...hour, to: startFromMidnightNextDay.to };
    }

    return hour;
  });

  return joinedOpeningHours?.length > 0 ? joinedOpeningHours : menuOpeningHours;
};

const dateToPickupSlotTime = (timestamp: DateArg, timeZone: string) =>
  format(utcToZonedTime(timestamp, timeZone), 'HH:mm');

const getMinutesSinceMidnight = (timeStr: string) => {
  const [hours, minutes] = timeStr.split(':').map(Number);

  return hours * 60 + minutes;
};

export const timeUtils = {
  calculateMenuOpeningHours,
  dateToPickupSlotTime,
  formatHoursInterval,
  getCurrentDayWorkHours,
  getCurrentWeekday,
  getMenuHours,
  getNextWeekDay,
  getNextWeekdayWorkHours,
  getWeekday,
  getWeekdayWorkHours,
  isMenuActive,
  isMenuWithinInterval,
  isStoreOpen,
  isWithinDates,
  isWithinInterval,
  parseTimeToDate,
  getMinutesSinceMidnight,
};
